import type { NativeModule } from "expo";

import type {
  HostInboundMessage,
  HostOutboundMessage,
} from "$modules/expo-tv-connect";

export {
  PlatformKeyCode,
  TV_CONNECT_PROTOCOL_VERSION,
} from "$modules/expo-tv-connect";

export type {
  Capabilities,
  HostInboundMessage,
  HostOutboundMessage,
  PlaybackState,
  VolumeAction,
} from "$modules/expo-tv-connect";

export type HostInboundMessageEvent = HostInboundMessage & {
  clientId: string;
};

export type ConfigureOptions = {
  /** Bonjour / NSD service type. */
  mdnsServiceType: string;
  /** Bonjour / NSD browse domain. */
  mdnsDiscoveryDomain: string;
};

export type StartHostingOptions = {
  /** Name advertised over Bonjour, e.g. the device's stable id. */
  serviceName: string;
  /** Optional Bonjour TXT record. */
  txt?: Record<string, string>;
};

export type HostingStartedEvent = {
  serviceName: string;
  serviceType: string;
  port: number;
};

export type HostingStoppedEvent = {
  serviceName: string;
  serviceType: string;
  port: number;
};

export type RemoteConnectedEvent = {
  clientId: string;
};

export type RemoteRequestEvent = {
  /** Stable id of the remote, used to remember prior approvals. */
  clientId: string;
};

export type RemoteDisconnectedEvent = {
  clientId: string;
  reason?: string;
};

export type ErrorEvent = {
  code: string;
  message: string;
  clientId?: string;
};

export type ExpoTvConnectHostModuleEvents = {
  onHostingStarted: (event: HostingStartedEvent) => void;
  onHostingStopped: (event: HostingStoppedEvent) => void;
  onRemoteRequest: (event: RemoteRequestEvent) => void;
  onRemoteConnected: (event: RemoteConnectedEvent) => void;
  onRemoteDisconnected: (event: RemoteDisconnectedEvent) => void;
  onMessage: (event: HostInboundMessageEvent) => void;
  onError: (event: ErrorEvent) => void;
};

export declare class ExpoTvConnectHostModule extends NativeModule<ExpoTvConnectHostModuleEvents> {
  /** Set mDNS advertise settings from `expo-tv-connect` TS constants. */
  configure(options: ConfigureOptions): void;
  /** Start hosting. */
  startHosting(options: StartHostingOptions): Promise<void>;
  /** Stop hosting. */
  stopHosting(): Promise<void>;
  /** Send a message to the connected remote. */
  send(clientId: string, message: HostOutboundMessage): void;
  /** Send a message to all connected remotes. */
  broadcast(message: HostOutboundMessage): void;
  /** Approve a pending remote, activating its session. */
  acceptRemote(clientId: string): void;
  /** Reject a pending remote before it is approved. */
  rejectRemote(clientId: string): void;
}
